import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Plus, X, Save, Sparkles, Pill } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function AdditiveAlerts({ 
  userAdditiveAlerts = [], 
  userVitaminAlerts = [], 
  userIngredientAlerts = [], 
  onSave 
}) {
  const [additiveAlerts, setAdditiveAlerts] = useState(userAdditiveAlerts);
  const [vitaminAlerts, setVitaminAlerts] = useState(userVitaminAlerts);
  const [ingredientAlerts, setIngredientAlerts] = useState(userIngredientAlerts);
  const [newAlert, setNewAlert] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('additives');

  const handleAddAlert = () => {
    if (!newAlert.trim()) return;
    if (activeTab === 'additives') {
      setAdditiveAlerts([...additiveAlerts, newAlert.trim()]);
    } else if (activeTab === 'vitamins') {
      setVitaminAlerts([...vitaminAlerts, newAlert.trim()]);
    } else if (activeTab === 'ingredients') {
      setIngredientAlerts([...ingredientAlerts, newAlert.trim()]);
    }
    setNewAlert('');
  };

  const handleRemoveAlert = (index, type) => {
    if (type === 'additives') {
      setAdditiveAlerts(additiveAlerts.filter((_, i) => i !== index));
    } else if (type === 'vitamins') {
      setVitaminAlerts(vitaminAlerts.filter((_, i) => i !== index));
    } else if (type === 'ingredients') {
      setIngredientAlerts(ingredientAlerts.filter((_, i) => i !== index));
    }
  };

  const handleSave = async () => {
    await onSave({
      additive_alerts: additiveAlerts,
      vitamin_alerts: vitaminAlerts,
      ingredient_alerts: ingredientAlerts
    });
    setEditMode(false);
    toast.success('Alertas actualizadas');
  };

  const renderAlertsList = (alerts, type, emptyMessage) => (
    <div className="space-y-2">
      {alerts.length === 0 ? (
        <p className="text-sm text-gray-500 py-4 text-center">{emptyMessage}</p>
      ) : (
        alerts.map((alert, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <span className="text-sm text-gray-700">{alert}</span>
            {editMode && (
              <button
                onClick={() => handleRemoveAlert(index, type)}
                className="w-6 h-6 rounded-full bg-white hover:bg-gray-100 flex items-center justify-center"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            )}
          </motion.div>
        ))
      )}
    </div>
  );

  return (
    <Card className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Mis Alertas</h3>
        </div>
        {!editMode ? (
          <Button size="sm" variant="outline" onClick={() => setEditMode(true)} className="flex-shrink-0 text-xs">
            Configurar
          </Button>
        ) : (
          <div className="flex gap-1.5 flex-shrink-0">
            <Button size="sm" variant="outline" className="text-xs px-2" onClick={() => {
              setAdditiveAlerts(userAdditiveAlerts);
              setVitaminAlerts(userVitaminAlerts);
              setIngredientAlerts(userIngredientAlerts);
              setEditMode(false);
            }}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700 text-xs px-2">
              <Save className="w-3.5 h-3.5 mr-1" />
              Guardar
            </Button>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 mb-4">
          <TabsTrigger value="additives" className="flex items-center gap-1 text-xs px-1">
            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">Aditivos</span>
          </TabsTrigger>
          <TabsTrigger value="vitamins" className="flex items-center gap-1 text-xs px-1">
            <Sparkles className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">Vitaminas</span>
          </TabsTrigger>
          <TabsTrigger value="ingredients" className="flex items-center gap-1 text-xs px-1">
            <Pill className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">Ingredientes</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="additives" className="space-y-3">
          {editMode && (
            <>
              <p className="text-sm text-gray-600">
                Recibe alertas cuando detectemos estos aditivos. Ej: "E250", "glutamato", "nitritos"
              </p>
              <div className="flex gap-2">
                <Input
                  value={newAlert}
                  onChange={(e) => setNewAlert(e.target.value)}
                  placeholder="Nombre o código del aditivo"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAlert()}
                />
                <Button onClick={handleAddAlert} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
          {renderAlertsList(additiveAlerts, 'additives', 'No tienes alertas de aditivos configuradas')}
        </TabsContent>

        <TabsContent value="vitamins" className="space-y-3">
          {editMode && (
            <>
              <p className="text-sm text-gray-600">
                Recibe alertas cuando encuentres productos con estas vitaminas. Ej: "B12", "D3", "Vitamina C"
              </p>
              <div className="flex gap-2">
                <Input
                  value={newAlert}
                  onChange={(e) => setNewAlert(e.target.value)}
                  placeholder="Nombre o código de vitamina"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAlert()}
                />
                <Button onClick={handleAddAlert} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
          {renderAlertsList(vitaminAlerts, 'vitamins', 'No tienes alertas de vitaminas configuradas')}
        </TabsContent>

        <TabsContent value="ingredients" className="space-y-3">
          {editMode && (
            <>
              <p className="text-sm text-gray-600">
                Recibe alertas cuando detectemos estos ingredientes. Ej: "gluten", "lactosa", "soja", "maní"
              </p>
              <div className="flex gap-2">
                <Input
                  value={newAlert}
                  onChange={(e) => setNewAlert(e.target.value)}
                  placeholder="Nombre del ingrediente"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAlert()}
                />
                <Button onClick={handleAddAlert} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
          {renderAlertsList(ingredientAlerts, 'ingredients', 'No tienes alertas de ingredientes configuradas')}
        </TabsContent>
      </Tabs>
    </Card>
  );
}